import PropTypes from 'prop-types';
import { GOOGLE_MAPS_LOADER_OPTIONS, VESSEL_MAP_OPTIONS } from '../../../utils/googleMapsConfig';
import { Alert, Button, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { GoogleMap, Marker, OverlayView, useJsApiLoader } from '@react-google-maps/api';
import { GetEmail } from '../../../Api/getData';
import useGlobalVessel from '../../../components/useGlobalVessel';
import useNearestProvinces from '../../Monitoring/useNearestProvinces';
import useDashboardResource from '../useDashboardResource';
import { parseLocation } from './location';
import { formatRefDate } from '../../../utils/formatRefDate';
import { useMemo, useState } from 'react';

const defaultCenter = { lat: -2.5489, lng: 118.0149 };
const loadLocation = (_, params, signal) => GetEmail(params, signal);

export default function Maps({ refresh = 0 }) {
    const vessel = useGlobalVessel();
    const [retry, setRetry] = useState(0);
    const location = useDashboardResource(loadLocation, '', { ObjectCode: vessel?.objectCode }, !!vessel, JSON.stringify([refresh, retry, vessel?.key]));
    const coordinates = location.data?.coordinates;
    const position = useMemo(() => parseLocation(coordinates), [coordinates]);
    const positionReports = useMemo(() => position ? [position] : [], [position]);
    const provinces = useNearestProvinces(positionReports);
    const province = position ? provinces.values[JSON.stringify([position.lat, position.lng])]?.name : '';
    const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
    const title = location.data?.Title || (vessel ? vessel.accountId + ' / ' + vessel.objectCode : 'Vessel');
    const time = location.data?.date && location.data?.time ? formatRefDate(location.data.date + ' ' + location.data.time) : '—';
    return <div className="dash-location">
        <div className="dash-location__coordinates">
            <span>Latitude <strong>{position ? position.lat.toFixed(6) : '—'}</strong></span>
            <span>Longitude <strong>{position ? position.lng.toFixed(6) : '—'}</strong></span>
            <span>Location <strong>{location.loading || provinces.loading
                ? <span className="dash-location__resolving" role="status"><LoadingOutlined spin aria-hidden="true" /> Loading...</span>
                : province || '—'}</strong></span>
        </div>
        {provinces.error && <p className="dash-location__lookup-error" role="status">Location could not be determined from the reported coordinates.</p>}
        {location.error && <Alert type="warning" showIcon message="Location could not be loaded" description="No position is shown until the location API returns valid coordinates." action={<Button size="small" onClick={() => setRetry(value => value + 1)}>Retry</Button>} />}
        {!location.loading && !location.error && !position && <Alert type="info" showIcon message="No valid vessel coordinates available." />}
        <div className="dash-location__map">
            {loadError ? <Alert type="error" message="Google Maps could not be loaded." /> : !isLoaded ? <div className="dash-location__loading"><Spin /></div> : <GoogleMap mapContainerStyle={{ width: '100%', height: '100%' }} center={position || defaultCenter} zoom={position ? 6 : 5} options={VESSEL_MAP_OPTIONS}>
                {position && <><Marker position={position} title={title} icon={{ url: '/assets/ship-z.png', scaledSize: new window.google.maps.Size(65, 65), anchor: new window.google.maps.Point(32, 32) }} /><OverlayView position={position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}><div className="dash-location__label">{title}</div></OverlayView></>}
            </GoogleMap>}
            {location.loading && <div className="dash-location__loading" role="status" aria-live="polite"><Spin /><span>Loading location</span></div>}
        </div>
        <p className="dash-location__time">Location report: {time}</p>
    </div>;
}
Maps.propTypes = { refresh: PropTypes.number };
